import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { ExternalLink } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useRankingParticipantCopy } from '@/i18n/rankingParticipant';
import { TableCell, TableRow } from '@/components/ui/table';
import { CampaignSubmissionTable } from './CampaignSubmissionTable';
import { sortSubmissionsNewestFirst } from '@/lib/campaigns/sortSubmissionsNewestFirst';
import { rankingReportBadge, rankingReportStatus, rankingReviewReason } from '@/lib/campaigns/rankingPresentation';
import type { RankingReport } from '@/queries/cpmRanking';
import { RankingReportDiagnostics } from './RankingReportDiagnostics';
import { RankingClipViews } from './RankingClipViews';

interface Props {
  reports: RankingReport[];
  timezone: string;
  canEditLegacy: boolean;
  onEditLegacy: (report: RankingReport) => void;
}

export function RankingOwnSubmissions({ reports, timezone, canEditLegacy, onEditLegacy }: Props) {
  const { t, i18n } = useTranslation();
  const copy = useRankingParticipantCopy();
  const titleId = useId();
  const legacyTitleId = useId();
  const locale = i18n?.resolvedLanguage || i18n?.language;
  const clips = sortSubmissionsNewestFirst(reports.filter(report => report.submission_id)
    .map(report => ({ report, createdAt: report.created_at }))).map(item => item.report);
  const legacy = reports.filter(report => !report.submission_id);
  const views = (value: string | null | undefined) => value == null ? '—' : BigInt(value).toLocaleString(locale);

  const platforms = [...new Set(clips.map(report => report.platform))].sort((a, b) => ['tiktok', 'instagram', 'youtube', 'facebook'].indexOf(a) - ['tiktok', 'instagram', 'youtube', 'facebook'].indexOf(b));
  const formatTime = (value: string | null | undefined) => value && Number.isFinite(Date.parse(value)) ? new Date(value).toLocaleString(locale, { timeZone: timezone }) : null;
  const canonicalRow = (report: RankingReport) => {
    const status = rankingReportStatus(report);
    const submitted = formatTime(report.created_at);
    const published = formatTime(report.published_at);
    const color = status === 'approved' ? 'text-primary border-primary/40 bg-primary/15' : status === 'rejected' ? 'text-red-400 border-red-500/40 bg-red-500/15' : 'text-amber-400 border-amber-500/40 bg-amber-500/10';
    const accent = status === 'approved' ? 'border-l-primary' : status === 'rejected' ? 'border-l-red-500' : 'border-l-amber-400';
    return <TableRow key={report.id} id={`campaign-submission-${report.submission_id}`} tabIndex={-1} className="scroll-mt-16">
      <TableCell className={`max-w-xs border-l-4 ${accent}`}>
        <a href={report.url} aria-label={report.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 truncate text-sm text-blue-400 hover:underline" title={report.url}><ExternalLink aria-hidden="true" className="h-3 w-3 shrink-0" /><span className="truncate">{report.url}</span></a>
        {submitted && <p className="mt-0.5 text-[10px] text-muted-foreground">{copy.submittedAt} {submitted}</p>}
        {published && <p className="mt-0.5 text-[10px] text-muted-foreground">{t('ranking.audit.columns.published')}: {published}</p>}
      </TableCell>
      <TableCell className="whitespace-nowrap"><span className={`inline-flex rounded border px-2 py-1 text-[10px] font-bold uppercase tracking-wide ${color}`}>{t(`ranking.clip.status.${status}`)}</span></TableCell>
      <TableCell className="min-w-[220px]"><div className="space-y-2">
        {report.review_reason && <p className="whitespace-pre-wrap text-sm text-muted-foreground">{rankingReviewReason(report.review_reason, t)}</p>}
        {report.evidence_url && <a className="inline-block text-sm underline" href={report.evidence_url} target="_blank" rel="noopener noreferrer">{t('ranking.evidence')}</a>}
        <RankingReportDiagnostics report={report} timezone={timezone} showLastSync={false} />
      </div></TableCell>
      <TableCell className="min-w-[280px] text-left"><RankingClipViews report={report} timezone={timezone} /></TableCell>
    </TableRow>;
  };

  const item = (report: RankingReport) => {
    const submittedAt = report.created_at ? Date.parse(report.created_at) : NaN;
    return <article key={report.id} id={`campaign-submission-${report.submission_id || report.id}`} tabIndex={-1} className="min-w-0 space-y-3 rounded-xl border bg-card p-4">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <Badge variant="outline">{t(`ranking.platforms.${report.platform}`)}</Badge>
        <Badge variant="outline">{t(`ranking.status.${rankingReportBadge(report)}`)}</Badge>
        {Number.isFinite(submittedAt) && <span className="text-xs text-muted-foreground">{copy.submittedAt} {new Date(submittedAt).toLocaleString(locale, { timeZone: timezone })}</span>}
      </div>
      <a href={report.url} aria-label={report.url} target="_blank" rel="noopener noreferrer" className="inline-flex max-w-full items-center gap-2 text-sm text-primary hover:underline">
        <ExternalLink aria-hidden="true" className="h-4 w-4 shrink-0" /><span className="truncate">{report.url}</span>
      </a>
      <p className="text-sm">{t('ranking.reported')}: {views(report.reported_views)} · {t('ranking.verified')}: {views(report.verified_views)}</p>
      {report.evidence_url && <a className="inline-block text-sm underline" href={report.evidence_url} target="_blank" rel="noopener noreferrer">{t('ranking.evidence')}</a>}
      <RankingReportDiagnostics report={report} timezone={timezone} />
      {report.review_reason && <p className="whitespace-pre-wrap text-sm text-muted-foreground">{rankingReviewReason(report.review_reason, t)}</p>}
      {canEditLegacy && report.status === 'rejected' && <Button variant="outline" size="sm" onClick={() => onEditLegacy(report)}>{t('ranking.edit')}</Button>}
    </article>;
  };

  return <>
    <section aria-labelledby={titleId} className="min-w-0 space-y-3">
      <h2 id={titleId} className="text-xl font-semibold">{copy.clipsTitle}</h2>
      <p className="text-sm text-muted-foreground">{copy.clipsHelp}</p>
      {clips.length ? <div className="space-y-6">{platforms.map(platform => <CampaignSubmissionTable key={platform} platform={platform} count={clips.filter(report => report.platform === platform).length} columns={[
        { label: t('campaign.submissions.clipLink') }, { label: t('campaign.submissions.verdict') }, { label: t('campaign.submissions.feedback') }, { label: copy.viewsTitle },
      ]}>{clips.filter(report => report.platform === platform).map(canonicalRow)}</CampaignSubmissionTable>)}<p className="text-center text-xs italic text-muted-foreground">{copy.provisionalDisclaimer}</p></div> : <p className="rounded-xl border border-dashed p-4 text-sm text-muted-foreground">{copy.clipsEmpty}</p>}
    </section>
    {legacy.length > 0 && <section aria-labelledby={legacyTitleId} className="min-w-0 space-y-3">
      <h2 id={legacyTitleId} className="text-xl font-semibold">{copy.legacyTitle}</h2>
      <p className="text-sm text-muted-foreground">{copy.legacyHelp}</p>
      {legacy.map(item)}
    </section>}
  </>;
}
